import React, { useEffect, useState, useCallback } from "react";
import { wasteApi } from "@/api/waste";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";

export default function WasteSettings({ venueId, settings, onUpdated }) {
  const [thresholds, setThresholds] = useState({
    alert_single_cost: 30,
    alert_daily_cost: 150,
    critical_product_count: 3,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.thresholds) {
      setThresholds({
        alert_single_cost: settings.thresholds.alert_single_cost ?? 30,
        alert_daily_cost: settings.thresholds.alert_daily_cost ?? 150,
        critical_product_count: settings.thresholds.critical_product_count ?? 3,
      });
    }
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await wasteApi.updateSettings({
        venue_id: venueId,
        thresholds: {
          alert_single_cost: parseFloat(thresholds.alert_single_cost) || 0,
          alert_daily_cost: parseFloat(thresholds.alert_daily_cost) || 0,
          critical_product_count: parseInt(thresholds.critical_product_count, 10) || 3,
        },
      });
      toast.success("Configuració desada");
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e.message || "Error guardant");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="waste-settings">
      <Card className="p-4 sm:p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Llindars d'alerta</h3>
          <p className="text-sm text-gray-500">Configura quan vols rebre avisos automàtics.</p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-semibold text-gray-700">Cost mínim alerta (€)</Label>
            <Input
              type="number" min="0" step="0.01"
              value={thresholds.alert_single_cost}
              onChange={(e) => setThresholds({ ...thresholds, alert_single_cost: e.target.value })}
              data-testid="waste-threshold-single"
            />
            <p className="text-xs text-gray-500 mt-1">Si un registre supera aquest cost, es crea una alerta.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-gray-700">Cost diari màxim (€)</Label>
            <Input
              type="number" min="0" step="0.01"
              value={thresholds.alert_daily_cost}
              onChange={(e) => setThresholds({ ...thresholds, alert_daily_cost: e.target.value })}
              data-testid="waste-threshold-daily"
            />
            <p className="text-xs text-gray-500 mt-1">Suma diària per disparar alerta.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-gray-700">Producte crític (mín. registres/30d)</Label>
            <Input
              type="number" min="1" step="1"
              value={thresholds.critical_product_count}
              onChange={(e) => setThresholds({ ...thresholds, critical_product_count: e.target.value })}
              data-testid="waste-threshold-critical"
            />
            <p className="text-xs text-gray-500 mt-1">Un producte amb N+ registres en 30 dies es considera crític.</p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]" data-testid="waste-settings-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </div>
      </Card>

      <Card className="p-4 sm:p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-3">Famílies, motius i zones</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-2">Famílies</p>
            <ul className="space-y-1 text-gray-700">
              {(settings?.product_families || []).map((f) => <li key={f.code}>• {f.label}</li>)}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-2">Motius</p>
            <ul className="space-y-1 text-gray-700">
              {(settings?.waste_reasons || []).map((r) => <li key={r.code}>• {r.label}</li>)}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-2">Zones</p>
            <ul className="space-y-1 text-gray-700">
              {(settings?.zones || []).map((z) => <li key={z.code}>• {z.label}</li>)}
            </ul>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3 italic">
          La personalització de famílies/motius/zones es podrà editar més endavant des d'aquesta mateixa pantalla.
        </p>
      </Card>
    </div>
  );
}
